import type { ReactElement } from 'react';
import { ChipToggle, Field, Input, Select } from '@/components/ui';
import type { RegisterField } from '@/features/registers/api';
import styles from './OperationalRecords.module.css';
import type { FormValue } from './register-format';

export function FieldInput({
  field: f,
  value,
  onChange,
  staffOptions,
  idPrefix,
  error,
}: {
  field: RegisterField;
  value: FormValue;
  onChange: (v: FormValue) => void;
  staffOptions: { value: string; label: string }[];
  idPrefix: string;
  /** RL-1826: the API's message for this field (RECORD_INVALID `field`). */
  error?: string;
}) {
  const id = `${idPrefix}-${f.key}`;
  const label = f.required ? f.label : `${f.label} (optional)`;
  if (f.type === 'multiselect' || f.type === 'boolean') {
    const options =
      f.type === 'boolean'
        ? [
            { value: 'yes', label: 'Yes' },
            { value: 'no', label: 'No' },
          ]
        : (f.options ?? []);
    const selected =
      f.type === 'boolean'
        ? [value === true ? 'yes' : 'no']
        : (value as string[]);
    return (
      <div className={styles.chipField}>
        <ChipToggle
          label={label}
          options={options}
          selected={selected}
          single={f.type === 'boolean'}
          onChange={(next) =>
            onChange(f.type === 'boolean' ? next[0] === 'yes' : next)
          }
        />
        {f.hint && <span className={styles.hint}>{f.hint}</span>}
        {error && (
          <span className={styles.late} role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }
  const v = typeof value === 'string' ? value : '';
  let control: ReactElement<Record<string, unknown>>;
  if (f.type === 'select' || f.type === 'staff')
    control = (
      <Select
        id={id}
        value={v}
        placeholder="Choose…"
        options={f.type === 'staff' ? staffOptions : (f.options ?? [])}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  else if (f.type === 'textarea')
    control = (
      <textarea
        id={id}
        className={styles.textarea}
        value={v}
        maxLength={f.maxLength ?? 4000}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  else
    control = (
      <Input
        id={id}
        value={v}
        type={
          f.type === 'date'
            ? 'date'
            : f.type === 'datetime'
              ? 'datetime-local'
              : 'text'
        }
        inputMode={
          f.type === 'number' || f.type === 'money' ? 'decimal' : undefined
        }
        maxLength={f.type === 'text' ? (f.maxLength ?? 200) : undefined}
        placeholder={f.type === 'money' ? 'RM' : undefined}
        onChange={(e) => onChange(e.target.value)}
      />
    );
  return (
    <Field label={label} hint={f.hint} error={error}>
      {control}
    </Field>
  );
}
